import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import { agentApi } from "../lib/apiClient";
import { showApiError } from "../lib/alerts";
import { appendUniqueById, readPagination } from "../lib/catalog";
import { CatalogSkeletonGrid } from "../components/catalog/CatalogSkeletons";
import TransportCard from "../components/transport/TransportCard";
import TransportFilterBar from "../components/transport/TransportFilterBar";
import {
  countTransportFilters,
  emptyTransportFilters,
  normalizeTariffs,
  transportMatchesFilters,
} from "../components/transport/transportCatalog";

const PAGE_SIZE = 15;

export default function TransportationPage() {
  const [searchParams] = useSearchParams();
  const [tariffs, setTariffs] = useState([]);
  const [filters, setFilters] = useState(emptyTransportFilters());
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [appliedSearch, setAppliedSearch] = useState(searchParams.get("q") || "");

  function transportQueryParams(pageNumber) {
    return {
      page: pageNumber,
      limit: PAGE_SIZE,
      search: appliedSearch.trim() || undefined,
    };
  }

  useEffect(() => {
    let alive = true;

    async function loadTariffs() {
      setLoading(true);
      try {
        const payload = await agentApi.get("/transport/tariff", {
          params: transportQueryParams(1),
        });
        if (!alive) return;
        const items = normalizeTariffs(payload);
        const pagination = readPagination(payload);
        setTariffs(items);
        setPage(1);
        setHasNext(pagination.hasNext);
        setTotal(pagination.total || items.length);
      } catch (fetchError) {
        if (!alive) return;
        setTariffs([]);
        setHasNext(false);
        showApiError(fetchError, "LOAD_TRANSPORT");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadTariffs();

    return () => {
      alive = false;
    };
  }, [appliedSearch]);

  useEffect(() => {
    const nextQuery = searchParams.get("q") || "";
    setSearch(nextQuery);
    setAppliedSearch(nextQuery);
  }, [searchParams]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setAppliedSearch((current) => {
        const next = search.trim();
        return current === next ? current : next;
      });
    }, 400);

    return () => window.clearTimeout(handle);
  }, [search]);

  function applySearch() {
    setAppliedSearch(search.trim());
  }

  async function loadMore() {
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const payload = await agentApi.get("/transport/tariff", {
        params: transportQueryParams(nextPage),
      });
      const items = normalizeTariffs(payload);
      const pagination = readPagination(payload);
      setTariffs((current) => appendUniqueById(current, items, (item) => item.id));
      setPage(nextPage);
      setHasNext(pagination.hasNext);
      setTotal(pagination.total || 0);
    } catch (fetchError) {
      showApiError(fetchError, "LOAD_TRANSPORT");
    } finally {
      setLoadingMore(false);
    }
  }

  const displayedTariffs = useMemo(
    () => tariffs.filter((item) => transportMatchesFilters(item, filters, search)),
    [tariffs, filters, search]
  );
  const hasActiveFilters = countTransportFilters(filters) > 0 || Boolean(search.trim());

  return (
    <AgentLayout>
      <div className="container-fluid py-24 package-catalog-page">
        <PageHeader
          title="Umrah Transport"
          items={[DASHBOARD_CRUMB, { label: "Services" }, { label: "Transport" }]}
        >
          <TransportFilterBar
            search={search}
            onSearchChange={setSearch}
            onSearchSubmit={applySearch}
            filters={filters}
            onFilterChange={setFilters}
          />
        </PageHeader>

        {loading ? <CatalogSkeletonGrid variant="transport" count={8} label="Loading live transportation..." /> : null}

        {!loading && displayedTariffs.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <h5 className="mb-2">No transport tariffs found</h5>
              <p className="text-secondary mb-0">
                {hasActiveFilters
                  ? "Try another type, fuel, price range or search name."
                  : "Try a different search term or refresh after tariffs are published in admin."}
              </p>
            </div>
          </div>
        ) : null}

        {!loading && displayedTariffs.length > 0 ? (
          <div className="package-catalog-grid">
            {displayedTariffs.map((item) => (
              <TransportCard key={item.id} item={item} />
            ))}
          </div>
        ) : null}

        {!loading && hasNext ? (
          <div className="text-center mt-5">
            <button type="button" className="btn btn-outline-primary" disabled={loadingMore} onClick={loadMore}>
              {loadingMore ? "Loading..." : `Load more ${total ? ` (${tariffs.length}/${total})` : ""}`}
            </button>
          </div>
        ) : null}
      </div>
    </AgentLayout>
  );
}
